import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/autoplay";
import { Autoplay } from "swiper/modules";
import AOS from "aos";
import "aos/dist/aos.css";

import HeroSlider from "../heroSlider/heroSlider";
import Footer from "../footer/footer";
import "./home.css";

const Home = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  /* ---------------------------------------------------
     ✨ Counter Restart Logic
  --------------------------------------------------- */
  const [counterKey, setCounterKey] = useState(0);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCounterKey((prev) => prev + 1); // restart counter
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  /* ---------------------------------------------------
      ✨ COUNTER HOOK
  --------------------------------------------------- */
  const useCounter = (end, duration = 2000) => {
    const [value, setValue] = useState(0);

    useEffect(() => {
      let start = 0;
      const step = Math.max(end / (duration / 36), 0.01);
      let rafId;

      const animate = () => {
        start += step;
        if (start < end) {
          setValue(start);
          rafId = requestAnimationFrame(animate);
        } else {
          setValue(end);
        }
      };

      rafId = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(rafId);
    }, [end, duration]);

    return value;
  };

  /* ---------------------------------------------------
      ✨ CHILD COMPONENT FOR CORRECT RESET BEHAVIOR
  --------------------------------------------------- */
  const StatNumber = ({ end, duration }) => {
    const value = useCounter(end, duration);
    const decimals = end % 1 === 0 ? 0 : 1;
    return <h2 className="stat-number">{value.toFixed(decimals)}</h2>;
  };

  /* ---------------------------------------------------
      TESTIMONIALS
  --------------------------------------------------- */
  const testimonials = [
    {
      img: "/files/MaleIcon.png",
      name: "Arun Kumar",
      role: "Event Client",
      text: "Amazing work — heartfelt appreciation! The fabric quality and finishing are absolutely top-notch.",
      color: "#0b6b5d",
    },
    {
      img: "/files/FemaleIcon.png",
      name: "Deepithi Kannan",
      role: "Corporate Partner",
      text: "Highly professional team with great creative inputs. The quality exceeded expectations.",
      color: "#0b6b5d",
    },
    {
      img: "/files/MaleIcon.png",
      name: "Krishan Nair",
      role: "Brand Client",
      text: "Their designs are unique and elegant. Great communication and on-time delivery every time.",
      color: "#0b6b5d",
    },
    {
      img: "/files/FemaleIcon.png",
      name: "Uma Nallasamy",
      role: "Corporate Client",
      text: "Totally satisfied — consistent quality and on-time delivery.",
      color: "#0b6b5d",
    },
    {
      img: "/files/MaleIcon.png",
      name: "Ravi Kumar",
      role: "Retail Partner",
      text: "They always deliver beyond expectations — their commitment to quality is evident in every project.",
      color: "#0b6b5d",
    },
    {
      img: "/files/FemaleIcon.png",
      name: "Priya Venkatesh",
      role: "Business Client",
      text: "Lis Mount team provides a wonderful experience. Excellent service and great results every time.",
      color: "#0b6b5d",
    },
  ];

  return (
    <div className="home-page">
      {/* HERO */}
      <HeroSlider />

      {/* ABOUT */}
      <section className="home-about-section" id="about">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} data-aos="fade-right">
              <img src="files/DSC06758.jpg" alt="About Linen Fabrics" className="about-image" />
              <p className="image-caption">Precision weaving at Lis Mount manufacturing unit.</p>
            </Col>

            <Col lg={6} data-aos="fade-left" style={{ paddingTop: "20px" }}>
              <h2 className="section-heading">Crafting the Future of Linen</h2>
              <p className="lead-text">
                We are pioneers in sustainable linen manufacturing — blending traditional craftsmanship
                with advanced technology. Recognized under the <strong>Startup India initiative</strong>,
                we redefine textile production through eco-conscious innovation and high-performance fabrics.
              </p>
              <Button href="/about" className="primary-btn mt-3">Learn More</Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ⭐⭐⭐ STATS SECTION ⭐⭐⭐ */}
      <section className="company-stats-section" ref={statsRef}>
        <Container>
          <Row className="align-items-center justify-content-center text-center">
            <Col md={10}>
              <div className="stats-wrapper" data-aos="fade-up" key={counterKey}>

                {/* Stat 1 */}
                <div className="stat-item" data-aos="fade-right">
                  <div className="stat-inline">
                    <StatNumber end={7} duration={2500} />
                    <span className="plus-sign">+</span>
                  </div>

                  <p className="stat-text">Years of R&D Experience</p>
                </div>

                <div className="divider-line"></div>

                {/* Stat 2 */}
                <div className="stat-item" data-aos="fade-left">
                  <StatNumber end={7.2} duration={2500} />
                  <p className="stat-text">Million Sq.m Delivered</p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* MANUFACTURING IMAGES */}
      <section className="manufacturing-section">
        <Container>
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="section-heading">Our Manufacturing Excellence</h2>
            <p className="lead-text">A glimpse inside our world-class production environment.</p>
          </div>

          <Row className="g-4">
            <Col md={4} data-aos="zoom-in">
              <div className="factory-box">
                <img src="files/DSC06635.jpg" className="factory-img" alt="inspection" />
                <p className="factory-caption">Fabric inspection & quality verification.</p>
              </div>
            </Col>

            <Col md={4} data-aos="zoom-in">
              <div className="factory-box">
                <img src="files/DSC06724 E.jpg" className="factory-img" alt="meter" />
                <p className="factory-caption">Precision meter counter ensuring accuracy.</p>
              </div>
            </Col>

            <Col md={4} data-aos="zoom-in">
              <div className="factory-box">
                <img src="files/DSC06790.jpg" className="factory-img" alt="loom" />
                <p className="factory-caption">High-performance weaving units in action.</p>
              </div>
            </Col>

            <Col md={4} data-aos="zoom-in">
              <div className="factory-box">
                <img src="files/DSC06881 E.jpg" className="factory-img" alt="yarn" />
                <p className="factory-caption">Premium dyed yarns ready for production.</p>
              </div>
            </Col>

            <Col md={4} data-aos="zoom-in">
              <div className="factory-box">
                <img src="files/DSC06891 E.jpg" className="factory-img" alt="worker" />
                <p className="factory-caption">Skilled workforce ensuring perfect weaving.</p>
              </div>
            </Col>

            <Col md={4} data-aos="zoom-in">
              <div className="factory-box">
                <img src="files/DSC06900 E.jpg" className="factory-img" alt="rolls" />
                <p className="factory-caption">Vibrant linen rolls ready for dispatch.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CLIENTELE */}
      <section className="clientele-section marquee-section">
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="section-heading">Our Esteemed Clientele</h2>
          <p className="lead-text">
            Trusted by global brands and industry leaders for quality and reliability.
          </p>
        </div>

        <div className="marquee">
          <div className="marquee-inner">
            {["Logo1.png", "Logo2.png", "Logo3.png", "Logo4.png", "Logo5.png", "Logo6.png", "Logo7.png", "Logo8.png", "Logo9.png", "Logo10.png"].map((Logo, idx) => (
              <div className="marquee-logo-box" key={idx}>
                <img src={`files/${Logo}`} alt={`Client ${idx + 1}`} className="marquee-logo" />
              </div>
            ))}

            {["Logo1.png", "Logo2.png", "Logo3.png", "Logo4.png", "Logo5.png", "Logo6.png", "Logo7.png", "Logo8.png", "Logo9.png", "Logo10.png"].map((Logo, idx) => (
              <div className="marquee-logo-box" key={`dup-${idx}`}>
                <img src={`files/${Logo}`} alt={`Client ${idx + 1}`} className="marquee-logo" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO VIDEO SECTION */}
      <section className="intro-video-section">
        <Container>
          <div className="text-center mb-4" data-aos="fade-up">
            <h2 className="section-heading">Inside Lis Mount</h2>
            <p className="lead-text">A quick glimpse into our production excellence and quality-driven process.</p>
          </div>

          <div className="video-wrapper" data-aos="zoom-in">
            <video
              className="intro-video"
              controls
              poster="files/DSC06758.jpg"
            >
              <source src="./files/IntroVideo2.mp4" type="video/mp4" />
            </video>
          </div>
        </Container>
      </section>


      {/* TESTIMONIALS */}
      <section className="testimonials-section" id="testimonials">
        <Container>
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="section-heading">Client Testimonials</h2>
            <p className="lead-text">We’re proud to share what our clients say about working with Lis Mount.</p>
          </div>

          <Swiper
            spaceBetween={30}
            slidesPerView={3}
            breakpoints={{ 0: { slidesPerView: 1 }, 768: { slidesPerView: 3 } }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            loop={true}
            modules={[Autoplay]}
            className="testimonial-swiper"
          >
            {testimonials.map((item, idx) => (
              <SwiperSlide key={idx}>
                <div
                  className="testimonial-card-v2"
                  style={{ "--accent-color": item.color }}
                >
                  <div className="testimonial-header">
                    <div className="testimonial-img-wrapper">
                      <img src={item.img} alt={item.name} className="testimonial-img" />
                    </div>
                  </div>

                  <div className="testimonial-body">
                    <p className="testimonial-text">“{item.text}”</p>
                    <h5 className="testimonial-name">{item.name}</h5>
                    <p className="testimonial-role">{item.role}</p>
                    <div className="stars">
                      <i className="bi bi-star-fill" />
                      <i className="bi bi-star-fill" />
                      <i className="bi bi-star-fill" />
                      <i className="bi bi-star-fill" />
                      <i className="bi bi-star-fill" />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
